import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Tag, Avatar, Descriptions, Divider, Image, Timeline } from "antd";
import { PhoneOutlined, CarOutlined, CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { API_DOMAIN } from "../../../constants";
import { getAgentColor, agentInitials, transmissionTypeLabel, engineLayoutLabel, drivetrainLabel, useQv4Theme } from "../../helpers";
import { CustomerProfileButton } from "../shared/CustomerProfileButton";

dayjs.extend(utc);

const fmtDate = (val) => val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "—";
const fmtAED = (val) => val != null ? `AED ${Number(val).toLocaleString()}` : "—";

const fmtElapsed = (ms) => {
  if (!ms || ms < 0) return null;
  const totalMins = Math.floor(ms / 60000);
  if (totalMins < 1) return "< 1m";
  if (totalMins < 60) return `${totalMins}m`;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hrs < 24) return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
};

const ElapsedDivider = ({ ms }) => {
  const { t } = useQv4Theme();
  const label = fmtElapsed(ms);
  if (!label) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "8px 0 2px", color: t.textSub }}>
      <div style={{ flex: 1, height: 1, background: t.border }} />
      <span style={{ fontSize: 10, whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: t.border }} />
    </div>
  );
};

const EVENT_CFG = {
  supplier_quote_received: { color: "green",  label: "Quote received" },
  sent_to_suppliers:       { color: "blue",   label: "Sent to suppliers" },
  draft_created:           { color: "gray",   label: "Draft created" },
  status_changed:          { color: "blue",   label: "Status changed" },
  unavailable_parts_notified: { color: "orange", label: "Notified: Unavailable Parts" },
  part_created:            { color: "blue",   label: "Part added" },
  images_added:            { color: "blue",   label: "Photos added" },
};

function TaskTimeline({ timeline, parts, userMap, taskStatus }) {
  const { t } = useQv4Theme();
  if (!timeline?.length) return null;

  const partNameById = {};
  const supplierNameById = {};
  parts.forEach(p => {
    if (p.id && p.part_name) partNameById[p.id] = p.part_name;
    (p.supplier_quotes ?? p.quotes ?? []).forEach(q => {
      if (q.supplier_id && q.supplier_name) supplierNameById[q.supplier_id] = q.supplier_name;
    });
  });

  const sorted = [...timeline].sort((a, b) => new Date(b.time) - new Date(a.time));

  const TERMINAL = new Set(["cancelled", "completed", "delivered", "disabled"]);
  const isCompleted = taskStatus && TERMINAL.has(taskStatus);
  const oldestMs  = new Date(sorted[sorted.length - 1].time).getTime();
  const newestMs  = new Date(sorted[0].time).getTime();
  const endMs     = isCompleted ? newestMs : Date.now();
  const totalMs   = endMs - oldestMs;
  const totalStr  = fmtElapsed(totalMs);

  const renderContent = (e, elapsedMs) => {
    const cfg = EVENT_CFG[e.event];
    const userName = e.user_id ? (userMap[e.user_id] ?? `User #${e.user_id}`) : null;
    const metaEl = (
      <div style={{ fontSize: 10, color: t.textSub, marginTop: 3, display: "flex", gap: 6 }}>
        {userName && <span style={{ color: t.textSub, fontWeight: 500 }}>{userName}</span>}
        {userName && <span>·</span>}
        <span>{fmtDate(e.time)}</span>
      </div>
    );

    let body;
    if (e.event === "supplier_quote_received") {
      const partName = partNameById[e.query_part_id] || `Part #${e.query_part_id}`;
      const supplierName = supplierNameById[e.supplier_id] || `Supplier #${e.supplier_id}`;
      body = (
        <>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{cfg?.label ?? "Quote received"}</div>
          <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>
            <span style={{ fontWeight: 500 }}>{supplierName}</span>{" · "}{partName}
          </div>
          <div style={{ fontSize: 11, color: "#389e0d", fontWeight: 600, marginTop: 1 }}>
            AED {Number(e.quoted_price || 0).toLocaleString()}
            {Number(e.delivery_cost) > 0 ? ` + AED ${e.delivery_cost} delivery` : ""}
            {e.warranty_days ? ` · ${e.warranty_days}d warranty` : ""}
          </div>
        </>
      );
    } else {
      const label = cfg?.label ?? e.event.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      body = (
        <>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
          {e.details && <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>{e.details}</div>}
        </>
      );
    }

    return (
      <div>
        {body}
        {metaEl}
        <ElapsedDivider ms={elapsedMs} />
      </div>
    );
  };

  return (
    <div>
      {totalStr && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: t.hover, borderRadius: 6,
          padding: "5px 10px", marginBottom: 10, fontSize: 11,
        }}>
          <ClockCircleOutlined style={{ color: t.textSub, fontSize: 12 }} />
          <span style={{ color: t.textSub }}>
            <strong style={{ color: t.text }}>{totalStr}</strong>
            {" total · "}
            {isCompleted ? `draft → ${taskStatus.replace(/_/g, " ")}` : "draft → now (ongoing)"}
          </span>
        </div>
      )}
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 10, color: t.textSub }}>
        Timeline ({timeline.length})
      </div>
      <Timeline
        items={sorted.map((e, i) => {
          const nextOlder = sorted[i + 1];
          const elapsedMs = nextOlder
            ? new Date(e.time).getTime() - new Date(nextOlder.time).getTime()
            : null;
          return {
            key: i,
            color: EVENT_CFG[e.event]?.color ?? "gray",
            children: renderContent(e, elapsedMs),
          };
        })}
      />
    </div>
  );
}

export function OverviewTab({ task, afterSales, parts, userMap }) {
  const { t } = useQv4Theme();
  const assignee = task.assigned_user;
  const assigneeName = assignee
    ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
    : null;
  const images = Array.isArray(task.images) ? task.images : [];
  const timeline = Array.isArray(task.timeline) ? task.timeline : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {assigneeName && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar style={{ backgroundColor: getAgentColor(assigneeName), fontSize: 13 }}>
            {agentInitials(assigneeName)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{assigneeName}</div>
            <div style={{ fontSize: 11, color: t.textSub }}>{assignee.user_type || "Staff"}</div>
          </div>
        </div>
      )}

      <Descriptions column={1} size="small" bordered
        labelStyle={{ width: 130, fontWeight: 600, fontSize: 12 }}
        contentStyle={{ fontSize: 12 }}>
        {task.customer?.customer_name && (
          <Descriptions.Item label="Customer">
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, width: "100%" }}>
              {task.customer.customer_name}
              {task.customer?.customer_mobile && <CustomerProfileButton phone={task.customer.customer_mobile} />}
            </span>
          </Descriptions.Item>
        )}
        {task.customer?.customer_real_name && task.customer.customer_real_name !== task.customer.customer_name && (
          <Descriptions.Item label="Real Name">{task.customer.customer_real_name}</Descriptions.Item>
        )}
        {task.customer?.customer_mobile && (
          <Descriptions.Item label="Mobile">
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <PhoneOutlined style={{ fontSize: 11 }} />
              {task.customer.customer_mobile}
            </span>
          </Descriptions.Item>
        )}
        {task.vehicle && (
          <Descriptions.Item label="Vehicle">
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <CarOutlined style={{ fontSize: 11 }} />
              {[task.vehicle.brand_name || (typeof task.vehicle.brand === "string" ? task.vehicle.brand : null),
                task.vehicle.model_name || (typeof task.vehicle.model === "string" ? task.vehicle.model : null),
                task.vehicle.variant,
                task.vehicle.year].filter(Boolean).join(" · ")}
            </span>
          </Descriptions.Item>
        )}
        {task.vehicle?.vin && (
          <Descriptions.Item label="VIN">{task.vehicle.vin}</Descriptions.Item>
        )}
        {task.vehicle?.transmission_type && (
          <Descriptions.Item label="Transmission">{transmissionTypeLabel(task.vehicle.transmission_type)}</Descriptions.Item>
        )}
        {task.vehicle?.transmission_code && (
          <Descriptions.Item label="Transmission Code">{task.vehicle.transmission_code}</Descriptions.Item>
        )}
        {task.vehicle?.engine_code && (
          <Descriptions.Item label="Engine Code">{task.vehicle.engine_code}</Descriptions.Item>
        )}
        {task.vehicle?.engine_displacement && (
          <Descriptions.Item label="Engine Displacement">{task.vehicle.engine_displacement}</Descriptions.Item>
        )}
        {task.vehicle?.engine_layout && (
          <Descriptions.Item label="Engine Layout">{engineLayoutLabel(task.vehicle.engine_layout)}</Descriptions.Item>
        )}
        {task.vehicle?.drivetrain && (
          <Descriptions.Item label="Drivetrain">{drivetrainLabel(task.vehicle.drivetrain)}</Descriptions.Item>
        )}
        {task.vehicle?.fuel_type && (
          <Descriptions.Item label="Fuel Type">{task.vehicle.fuel_type}</Descriptions.Item>
        )}
        {task.vehicle?.country_spec && (
          <Descriptions.Item label="Country Spec">{task.vehicle.country_spec}</Descriptions.Item>
        )}
        {task.vehicle?.trim_info && (
          <Descriptions.Item label="Trim Info">{task.vehicle.trim_info}</Descriptions.Item>
        )}
        {task.vehicle?.notes && (
          <Descriptions.Item label="Vehicle Notes">{task.vehicle.notes}</Descriptions.Item>
        )}
        {task.created_at && (
          <Descriptions.Item label="Created">{fmtDate(task.created_at)}</Descriptions.Item>
        )}
      </Descriptions>

      {images.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: t.textSub }}>
            Images ({images.length})
          </div>
          <Image.PreviewGroup>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {images.map((src, i) => {
                const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
                return (
                  <Image key={i} src={url} width={80} height={80}
                    style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0, cursor: "pointer", border: `1px solid ${t.border}` }}
                    preview={{ src: url }}
                  />
                );
              })}
            </div>
          </Image.PreviewGroup>
        </div>
      )}

      <TaskTimeline timeline={timeline} parts={parts} userMap={userMap} taskStatus={task.status} />

      {task.remarks && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: t.textSub }}>Remarks</div>
          <div style={{ fontSize: 12, color: t.textSub, whiteSpace: "pre-wrap", background: t.headerBg, padding: "8px 10px", borderRadius: 6, border: `1px solid ${t.border}` }}>
            {task.remarks}
          </div>
        </div>
      )}

      {afterSales && (
        <div>
          <Divider style={{ margin: "4px 0 12px" }} />
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCircleOutlined style={{ color: "#52c41a" }} /> Sale Confirmed
          </div>
          <Descriptions column={2} size="small" bordered
            labelStyle={{ fontWeight: 600, fontSize: 12 }}
            contentStyle={{ fontSize: 12 }}>
            {afterSales.so_number && (
              <Descriptions.Item label="SO #" span={afterSales.po_number ? 1 : 2}>
                {afterSales.so_url
                  ? <a href={afterSales.so_url} target="_blank" rel="noreferrer">{afterSales.so_number}</a>
                  : afterSales.so_number}
              </Descriptions.Item>
            )}
            {afterSales.po_number && (
              <Descriptions.Item label="PO #">
                {afterSales.po_url
                  ? <a href={afterSales.po_url} target="_blank" rel="noreferrer">{afterSales.po_number}</a>
                  : afterSales.po_number}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Sale">{fmtAED(afterSales.sale_amount)}</Descriptions.Item>
            <Descriptions.Item label="Paid">{fmtAED(afterSales.paid_amount)}</Descriptions.Item>
            <Descriptions.Item label="Balance">{fmtAED(afterSales.balance_amount)}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={afterSales.payment_status === "paid" ? "green" : afterSales.payment_status === "partial" ? "orange" : afterSales.payment_status === "credit" ? "blue" : "red"} style={{ margin: 0 }}>
                {afterSales.payment_status_label || afterSales.payment_status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}
    </div>
  );
}
